import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { ExternalLink, Mail, RefreshIcon } from 'assets/icons';
import Button from 'components/button';
import SlideOverBody from 'components/SlideOver/component/body';
import SlideOverFooter from 'components/SlideOver/component/footer';
import SlideOverHeader from 'components/SlideOver/component/header';
import useSlideOver from 'components/SlideOver/useSlideOver';
import {
  INVOICE_LIST_STALE,
  fillText,
} from 'components/invoiceImport/invoiceImportText';
import InvoiceStatusBadge from 'components/invoiceList/InvoiceStatusBadge';
import { downloadFile } from 'components/invoiceList/useInvoiceExport';
import {
  INVOICE_MAIL_SENT,
  invoiceSendMailText,
} from 'components/invoiceSendMail/invoiceSendMailText';
import {
  INVOICE_LITIGATION_MODAL,
  INVOICE_REMINDER_MODAL,
  invoiceRemindersText,
} from 'components/invoiceReminders/invoiceRemindersText';
import useInvoiceReminderChanged from 'components/invoiceReminders/useInvoiceReminderChanged';
import useModal from 'components/modal/useModal';
import PillTabs from 'components/tabs/PillTabs';
import { isSendableInvoiceStatus } from 'constants/invoiceSending';
import UseContext from 'hooks/useContext';
import ApiDatabase from 'server';
import InvoiceCommentsTab from './InvoiceCommentsTab';
import InvoiceDetailInformation from './InvoiceDetailInformation';
import InvoiceHistoryTab from './InvoiceHistoryTab';
import InvoicePdfPreview from './InvoicePdfPreview';
import { companyLabelOf, toStatusBadgeInvoice } from './invoiceDetailText';
import { useInvoiceDetail, useInvoicePdf } from './useInvoiceDetail';
import useInvoiceComments from './useInvoiceComments';
import useInvoiceDetailImport from './useInvoiceDetailImport';

const DownloadIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M12 4v11m0 0l-4-4m4 4l4-4M5 19h14"
      stroke="#374151"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const RELOAD_FAILED_TOAST_ID = 'invoice-detail-reload-failed';

// The statuses shown of an invoice an admin may remind by hand, late or not
// ("late" comes on top of a status). The api decides the rest (litigation,
// no due date…) and the modal shows its refusal.
const REMINDABLE_INVOICE_STATUSES = ['pending', 'partially_paid'];

const isRemindableInvoice = (invoice) =>
  Boolean(invoice) &&
  invoice.kind !== 'credit_note' &&
  invoice.importStatus === 'imported' &&
  REMINDABLE_INVOICE_STATUSES.includes(invoice.status);

// Litigation takes a late invoice only (409 INVOICE_NOT_LATE otherwise). The
// detail does not know whether it is in litigation already: the api's 409
// ALREADY_IN_LITIGATION says so in the confirmation (JBWEB-1284).
const canStartLitigationOf = (invoice) =>
  isRemindableInvoice(invoice) && invoice.lateDays > 0;

// The main action of the footer for each kind of invoice.
const mainActionOf = (invoice) => {
  if (invoice.status === 'error') return 'retry';
  if (invoice.importStatus !== 'imported') return 'import';
  return invoice.pennylane?.url ? 'pennylane' : null;
};

const InvoiceDetailView = ({ number, onClose }) => {
  const { language, setModalData, modalDataReturn, setModalDataReturn } =
    UseContext();
  const { toggle: toggleModal } = useModal();
  const text = useCallback(
    (key, fallback) => language.getValue(`invoiceDetail.${key}`, fallback),
    [language],
  );
  // The provider rebuilds `language` on every render, so `text` changes with
  // every context update: effects read it through a ref.
  const textRef = useRef(text);
  textRef.current = text;
  const panelRef = useRef(null);
  const [tab, setTab] = useState('information');
  const [categories, setCategories] = useState(null);
  const [historyKey, setHistoryKey] = useState(0);
  const detail = useInvoiceDetail(number);
  const pdf = useInvoicePdf(number);
  const comments = useInvoiceComments(number);
  const { invoice } = detail;

  useEffect(() => {
    ApiDatabase.getPennyLaneCategories(
      {},
      (data) => setCategories(data?.items ?? []),
      (error) => {
        if (error?.name === 'CanceledError') return;
        // Without the list, a category is named by its number.
        setCategories([]);
      },
    );
  }, []);

  // A reload that fails over an invoice already shown keeps it, and says so.
  useEffect(() => {
    if (detail.failure && invoice) {
      toast.error(
        textRef.current(
          'reloadFailed',
          'La facture n’a pas pu être relue, rechargez-la.',
        ),
        { toastId: RELOAD_FAILED_TOAST_ID },
      );
    }
  }, [detail.failure, invoice]);

  // The focus moves into the panel when it opens.
  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  const { reload: reloadDetail, replace: replaceInvoice } = detail;

  // The modal of the category and the term answers with the invoice as the
  // api wrote it, on a success and on the refusals that still wrote part of
  // it, in the same return that tells the month's list to read itself again
  // (the list reacts in this same commit, before the reset). When the api
  // could not read the invoice back after writing, the answer holds the
  // fields sent only, without `status`: the panel reads the invoice again
  // rather than keep a stale status and lateness.
  useEffect(() => {
    if (modalDataReturn?.type !== INVOICE_LIST_STALE) return;
    if (!('invoice' in modalDataReturn)) return;
    if (Number(modalDataReturn.number) === number) {
      if (modalDataReturn.invoice?.status) {
        replaceInvoice(modalDataReturn.invoice);
      } else {
        reloadDetail();
      }
    }
    setModalDataReturn({});
  }, [
    modalDataReturn,
    number,
    replaceInvoice,
    reloadDetail,
    setModalDataReturn,
  ]);

  // A mail sent writes a « Facture envoyée par mail » line in the history.
  // The page's list reacts to the same return: nobody resets it.
  useEffect(() => {
    if (modalDataReturn?.type !== INVOICE_MAIL_SENT) return;
    if ((modalDataReturn.invoiceNumbers ?? []).map(Number).includes(number)) {
      setHistoryKey((key) => key + 1);
    }
  }, [modalDataReturn, number]);

  // A reminder sent writes a line in the history, and a refusal may come
  // with a status the api has just written (paid in Pennylane meanwhile). A
  // move to litigation fires the same return, and writes its own line.
  useInvoiceReminderChanged((changedNumber) => {
    if (changedNumber !== number) return;
    reloadDetail();
    setHistoryKey((key) => key + 1);
  });

  const openInformationEditor = () => {
    setModalData({ invoice, categories });
    toggleModal('invoice_information');
  };
  // Confirming an appendix can close the invoice's ERR_PENNYLANE_ANNEXE: the
  // invoice, its history and the month's list are read again.
  const onAppendixConfirmed = useCallback(() => {
    reloadDetail();
    setHistoryKey((key) => key + 1);
    setModalDataReturn({ type: INVOICE_LIST_STALE });
  }, [reloadDetail, setModalDataReturn]);
  const { reload: reloadPdf, blob } = pdf;
  // At the end of a run the invoice has changed; its PDF only matters if it
  // could not be shown (an import was running, BeSTT failed).
  const onRunFinished = useCallback(() => {
    reloadDetail();
    if (!blob) reloadPdf();
    setHistoryKey((key) => key + 1);
  }, [reloadDetail, reloadPdf, blob]);
  const actions = useInvoiceDetailImport(invoice, onRunFinished);

  let title = fillText(text('titleLoading', 'N° {number}'), { number });
  if (invoice) {
    title = fillText(text('title', 'N° {number} · {company}'), {
      number: invoice.number,
      company: companyLabelOf(text, invoice),
    });
  }

  let body;
  if (!invoice && detail.failure) {
    body = (
      <div
        role="alert"
        className="flex flex-col items-center gap-3 py-16 text-center text-sm text-gray-700"
      >
        <div>
          {detail.failure === 'notFound'
            ? text('notFound', 'Cette facture est introuvable.')
            : text('loadFailed', 'La facture n’a pas pu être chargée.')}
        </div>
        {detail.failure === 'failed' && (
          <Button color="SECONDARY" onClick={reloadDetail}>
            {text('reload', 'Recharger')}
          </Button>
        )}
      </div>
    );
  } else if (!invoice) {
    body = (
      <div role="status" className="py-16 text-center text-sm text-gray-500">
        {text('loading', 'Chargement de la facture…')}
      </div>
    );
  } else if (tab === 'information') {
    body = (
      <InvoiceDetailInformation
        invoice={invoice}
        categories={categories}
        text={text}
        onEdit={openInformationEditor}
        onAppendixConfirmed={onAppendixConfirmed}
      />
    );
  } else if (tab === 'history') {
    body = (
      <InvoiceHistoryTab
        invoice={invoice}
        categories={categories}
        refreshKey={historyKey}
      />
    );
  } else {
    body = <InvoiceCommentsTab comments={comments} />;
  }

  const mainAction = invoice ? mainActionOf(invoice) : null;
  const footer = (key, fallback) => text(`footer.${key}`, fallback);
  const mailText = invoiceSendMailText(language);
  const canSendByMail =
    Boolean(invoice) && isSendableInvoiceStatus(invoice.status);
  // One modal at a time in the Admin: it opens over the panel, as 1275's does.
  const openMail = () => {
    setModalData({ invoiceNumbers: [number] });
    toggleModal('invoice_send_mail');
  };
  const canRemind = isRemindableInvoice(invoice);
  const openReminder = () => {
    setModalData({ number });
    toggleModal(INVOICE_REMINDER_MODAL);
  };
  const canStartLitigation = canStartLitigationOf(invoice);
  const openLitigation = () => {
    setModalData({ number, action: 'start' });
    toggleModal(INVOICE_LITIGATION_MODAL);
  };
  const remindersText = invoiceRemindersText(language);
  let mainButton = null;
  if (mainAction === 'pennylane') {
    mainButton = (
      <a
        href={invoice.pennylane.url}
        target="_blank"
        rel="noopener noreferrer"
        className="h-12 flex items-center gap-2 px-4 rounded-md bg-yellow-500 text-white hover:bg-yellow-600"
      >
        {footer('viewInPennylane', 'Voir dans Pennylane')}
        <ExternalLink wh={18} color="#FFFFFF" />
      </a>
    );
  } else if (mainAction) {
    const isRetry = mainAction === 'retry';
    let label = isRetry
      ? footer('retry', 'Réessayer l’import')
      : footer('import', 'Importer dans Pennylane');
    if (actions.isRunning) label = footer('running', 'Import en cours…');
    mainButton = (
      <Button
        size="LG"
        color={actions.isBusy ? 'DISABLED' : 'PRIMARY'}
        disabled={actions.isBusy}
        onClick={isRetry ? actions.retryInvoice : actions.importInvoice}
      >
        {isRetry && !actions.isRunning && (
          <RefreshIcon wh={18} color="currentColor" />
        )}
        {label}
      </Button>
    );
  }

  return (
    <div className="flex flex-1 min-h-0">
      <InvoicePdfPreview
        pdf={pdf}
        isLive={invoice?.importStatus === 'discovered'}
        text={text}
      />
      <div
        ref={panelRef}
        role="region"
        aria-label={title}
        tabIndex={-1}
        className="flex flex-col w-1/2 min-w-[420px] min-h-0 border-l border-gray-100 outline-none"
      >
        <SlideOverHeader
          hide={onClose}
          closeLabel={language.getValue('invoiceImport.close', 'Fermer')}
          extraActions={
            invoice && (
              <InvoiceStatusBadge invoice={toStatusBadgeInvoice(invoice)} />
            )
          }
        >
          <span className="block text-xs font-normal uppercase tracking-wide text-gray-500">
            {text('kicker', 'Facture client')}
          </span>
          <span className="block ft-lg">{title}</span>
        </SlideOverHeader>
        <div className="flex-shrink-0 px-5 pb-2">
          <PillTabs
            tabs={[
              {
                id: 'information',
                label: text('tabInformation', 'Informations'),
              },
              { id: 'history', label: text('tabHistory', 'Historique') },
              {
                id: 'comments',
                label: comments.count
                  ? fillText(
                      language.getValue(
                        'invoiceComments.tabWithCount',
                        'Commentaires ({count})',
                      ),
                      { count: comments.count },
                    )
                  : text('tabComments', 'Commentaires'),
              },
            ]}
            activeTab={tab}
            onChange={setTab}
          />
        </div>
        <SlideOverBody>{body}</SlideOverBody>
        <div className="flex-shrink-0 border-t border-gray-100">
          <SlideOverFooter>
            <Button
              size="ICON"
              color="SECONDARY"
              aria-label={footer('download', 'Télécharger le PDF')}
              title={footer('download', 'Télécharger le PDF')}
              disabled={!blob}
              className="h-12 w-12 disabled:opacity-40 disabled:cursor-not-allowed"
              onClick={() => downloadFile(blob, `facture-${number}.pdf`)}
            >
              <DownloadIcon />
            </Button>
            {canSendByMail && (
              <Button
                size="ICON"
                color="SECONDARY"
                aria-label={mailText('open')}
                title={mailText('open')}
                className="h-12 w-12"
                onClick={openMail}
              >
                <Mail wh={20} color="#374151" />
              </Button>
            )}
            {canRemind && (
              <Button size="LG" color="SECONDARY" onClick={openReminder}>
                {remindersText('detail.remind')}
              </Button>
            )}
            {canStartLitigation && (
              <Button size="LG" color="SECONDARY" onClick={openLitigation}>
                {remindersText('litigation.start')}
              </Button>
            )}
            {mainButton}
          </SlideOverFooter>
        </div>
      </div>
    </div>
  );
};

/**
 * The detail of a client invoice (JBWEB-1274, mockups 3.1 to 3.6), the
 * slide-over `get_invoice`: its PDF on the left, its information on the
 * right, and what can be done with it in the footer. `objID` is its BeSTT
 * number; each number mounts the view anew, tab and zoom included.
 */
const InvoiceDetailPanel = () => {
  const { objID, removeAllParameters } = UseContext();
  const { toggleSlideOver } = useSlideOver();
  const number = Number(objID);
  const close = () => {
    toggleSlideOver();
    removeAllParameters?.();
  };
  if (!Number.isInteger(number) || number <= 0) return null;
  return <InvoiceDetailView key={number} number={number} onClose={close} />;
};

export default InvoiceDetailPanel;
